// Broche API (Cloud Run) for registration inventory.
// Until those routes are deployed, 404s fall back to the still-live Cloud Functions.
// Stripe deposit checkout stays on Cloud Functions.

const DEFAULT_API = 'https://api.brocheballetonline.com'
const LOCAL_API = 'http://localhost:3001'
const CF_PROD = 'https://us-central1-broche-ballet-online.cloudfunctions.net'

const CF_FALLBACK = {
  '/iabf/registration/hold': '/createRegistrationHold',
  '/iabf/registration/release': '/releaseRegistrationHold',
  '/iabf/registration/lookup-email': '/lookupRegistrationEmail',
  '/iabf/registration/availability': '/getRegistrationRoomAvailability'
}

function isLocalHost () {
  if (typeof window === 'undefined') {
    return false
  }
  return /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
}

export function brocheApiBase () {
  const fromEnv = String(process.env.VUE_APP_BROCHE_API_URL || '').trim()
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '')
  }
  // Local dashboard talks to the local Broche API so we can test before Cloud Run.
  if (isLocalHost()) {
    return LOCAL_API
  }
  return DEFAULT_API
}

function useCloudFunctionFallback () {
  // Don't hide local API failures by calling production Cloud Functions.
  return brocheApiBase() === DEFAULT_API
}

function unwrapBrocheResponse (res, json) {
  const nested = json && json.data && typeof json.data === 'object' ? json.data : null
  const data = nested || json || {}
  return {
    ok: res.ok && json.success !== false,
    status: res.status,
    data,
    message: json.message || data.message || data.error || ''
  }
}

function isMissingRoute (res, json) {
  if (!res || res.status === 404) {
    return true
  }
  const hay = [
    json && json.message,
    json && json.error,
    typeof json === 'string' ? json : ''
  ].join(' ')
  return /cannot post|cannot get/i.test(hay)
}

async function parseJson (res) {
  const json = await res.json().catch(() => ({}))
  return json && typeof json === 'object' ? json : {}
}

async function postJson (url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {})
  })
  return { res, json: await parseJson(res) }
}

async function getJson (url) {
  const res = await fetch(url, { method: 'GET' })
  return { res, json: await parseJson(res) }
}

/**
 * POST JSON to the Broche API. Unwraps `{ success, data }`.
 * Falls back to Cloud Functions if the API route is not deployed yet.
 */
export async function postBrocheApi (path, body) {
  try {
    const first = await postJson(brocheApiBase() + path, body)
    if (!isMissingRoute(first.res, first.json) || !useCloudFunctionFallback()) {
      return unwrapBrocheResponse(first.res, first.json)
    }
  } catch (e) {
    if (!useCloudFunctionFallback()) {
      throw e
    }
    console.warn('Broche API POST failed, trying Cloud Function', path, e)
  }
  const cfPath = CF_FALLBACK[path]
  if (!cfPath) {
    return {
      ok: false,
      status: 404,
      data: {},
      message: 'Could not reach the registration server. Please try again.'
    }
  }
  const fallback = await postJson(CF_PROD + cfPath, body)
  return unwrapBrocheResponse(fallback.res, fallback.json)
}

export async function getBrocheApi (path, params) {
  let url = brocheApiBase() + path
  let qs = ''
  if (params && typeof params === 'object') {
    const search = new URLSearchParams()
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        search.append(key, String(params[key]))
      }
    })
    qs = search.toString()
    if (qs) {
      url += (url.indexOf('?') === -1 ? '?' : '&') + qs
    }
  }
  try {
    const first = await getJson(url)
    if (!isMissingRoute(first.res, first.json) || !useCloudFunctionFallback()) {
      return unwrapBrocheResponse(first.res, first.json)
    }
  } catch (e) {
    if (!useCloudFunctionFallback()) {
      throw e
    }
    console.warn('Broche API GET failed, trying Cloud Function', path, e)
  }
  const cfPath = CF_FALLBACK[path]
  if (!cfPath) {
    return {
      ok: false,
      status: 404,
      data: {},
      message: 'Could not reach the registration server. Please try again.'
    }
  }
  const fallback = await getJson(CF_PROD + cfPath + (qs ? '?' + qs : ''))
  return unwrapBrocheResponse(fallback.res, fallback.json)
}

export function createRegistrationHold (body) {
  return postBrocheApi('/iabf/registration/hold', body)
}

export function releaseRegistrationHold (holdId) {
  return postBrocheApi('/iabf/registration/release', { holdId })
}

export function lookupRegistrationEmail (email) {
  return postBrocheApi('/iabf/registration/lookup-email', { email })
}

export function getRegistrationRoomAvailability (opts) {
  const params = {}
  if (opts && opts.reconcile) {
    params.reconcile = '1'
  }
  if (opts && opts.recount) {
    params.recount = '1'
  }
  return getBrocheApi('/iabf/registration/availability', params)
}
